/**
 * @fileoverview RiddleLayoutOnlyOptions - Formato 4 (Solo Opciones):
 * Cuadrícula CSS Grid de 2x2 (2 filas de 2 columnas) con botones de opciones gigantes centrados.
 * Diseñado para una legibilidad máxima y foco total en las opciones desde cualquier ángulo del estadio.
 * Aceleración por GPU (Layer Compositing), alta visibilidad en 1080p y 4K.
 * 
 * @module components/Screen/Sections/Riddle/RiddleLayoutOnlyOptions
 */

import React from 'react';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Componente Layout Solo Opciones (Grid 2x2) para la Pantalla Gigante de Adivina Adivinador.
 * 
 * @param {Object} props
 * @param {Object} props.riddle - Datos de la adivinanza (options, response, instruction, etc.).
 * @param {boolean} props.isItemVisible - Indica si la silueta/respuesta ya fue revelada.
 * @param {boolean} props.showCorrectAnswer - Indica si se debe resaltar la opción correcta.
 * @param {string} [props.fontFamily] - Familia tipográfica institucional.
 * @param {string} [props.textColor] - Color principal de los textos.
 * @returns {React.ReactElement}
 */
export const RiddleLayoutOnlyOptions = ({
    riddle,
    isItemVisible = false,
    showCorrectAnswer = true,
    fontFamily = '"GFS Didot", serif',
    textColor = '#FFFFFF'
}) => {
    const options = Array.isArray(riddle?.options) ? riddle.options : [];
    const responseText = riddle?.response?.text || '';

    // Determinar si la respuesta coincide para resaltar
    const isOptionCorrect = (option) => {
        if (option?.isCorrect === true) return true;
        if (responseText && option?.text && option.text.trim().toLowerCase() === responseText.trim().toLowerCase()) {
            return true;
        }
        return false;
    };

    return (
        <section className="RiddleLayout RiddleLayout--OnlyOptions">
            <div className="RiddleLayout__GridContainer">
                <div className="RiddleLayout__OptionsGrid">
                    {options.map((option, index) => {
                        const correct = isOptionCorrect(option);
                        const shouldHighlight = isItemVisible && showCorrectAnswer && correct;
                        const label = OPTION_LABELS[index] || `${index + 1}`;

                        return (
                            <div
                                key={option.id ?? index}
                                className={`RiddleLayout__GridOptionCard ${shouldHighlight ? 'RiddleLayout__GridOptionCard--Correct' : ''} ${isItemVisible && !correct && showCorrectAnswer ? 'RiddleLayout__GridOptionCard--Dimmed' : ''}`}
                            >
                                <div className="RiddleLayout__GridOptionCard__Badge">
                                    {label}
                                </div>

                                <div className="RiddleLayout__GridOptionCard__Content">
                                    <span
                                        className="RiddleLayout__GridOptionCard__Text"
                                        style={{ fontFamily, color: textColor }}
                                    >
                                        {option.text}
                                    </span>
                                </div>

                                {shouldHighlight && (
                                    <div className="RiddleLayout__GridOptionCard__CheckBadge">
                                        <i className="bi bi-check-circle-fill me-2" />
                                        <span>RESPUESTA CORRECTA</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default RiddleLayoutOnlyOptions;
